import type { VoidFunctionComponent } from 'react';

import Badge, { BadgeColors } from '../../../generic/Badge/Badge';
import FixBadge from '../../../issues/IssuesInbox/IssueItem/FixBadge';
import type { UpgradeDistance } from '../../../../../shared/vulns/types';

import styles from './RemediationPanels.module.less';

const remediationTypes = ['current', 'complete', 'partial', 'no-fix', 'unable-to-calculate'] as const;
type RemediationType = typeof remediationTypes[number];

const REMEDIATION_NAME: Record<RemediationType, string> = {
  current: 'CURRENT VERSION',
  complete: 'COMPLETE FIX',
  partial: 'PARTIAL FIX',
  'no-fix': 'NO FIX',
  'unable-to-calculate': 'UNABLE TO CALCULATE',
};

const REMEDIATION_TEXT: Record<RemediationType, string> = {
  current: 'You are exposed to this vulnerability with this version.',
  complete: 'Nearest update to fix all vulnerabilities found on this dependency.',
  partial: 'Nearest update to fix this vulnerability. This may not remediate all vulnerabilities.',
  'no-fix': 'FOSSA cannot find a fix for this vulnerability at this time.',
  'unable-to-calculate': 'FOSSA cannot calculate a fix for this vulnerability.',
};

const REMEDIATION_STYLE: Record<RemediationType, string> = {
  current: styles.remediationSuggestionCurrentVersion,
  complete: styles.remediationSuggestionCompleteFix,
  partial: styles.remediationSuggestionPartialFix,
  'no-fix': styles.remediationSuggestionNoFix,
  // share the same style as 'no-fix' because they are both unfixable
  'unable-to-calculate': styles.remediationSuggestionNoFix,
};

const REMEDIATION_BADGE_COLOR: Record<RemediationType, BadgeColors> = {
  current: BadgeColors.LightRed,
  complete: BadgeColors.DarkGreen,
  partial: BadgeColors.LightGreen,
  'no-fix': BadgeColors.LightRed,
  // share the same color as 'no-fix' because they are both unfixable
  'unable-to-calculate': BadgeColors.LightRed,
};

type Props = {
  type: RemediationType;
  version?: string;
  upgradeDistance?: UpgradeDistance;
  useFixBadge?: boolean;
};

//  Helper which returns the heading text, depending on the type.
const getHeading = ({ type, version }: Props): `Current version ${string}` | 'No fix available' | `Upgrade to ${string}` | 'Unable to calculate' => {
  switch (type) {
    case 'current':
      return `Current version ${version}`;
    case 'no-fix':
      return 'No fix available';
    case 'unable-to-calculate':
      return 'Unable to calculate';
    default:
      return `Upgrade to ${version}`;
  }
};

/**
 * PackageVersionVulnerabilityDrawerRemediationSuggestion shows a panel of
 * specific information detailing one of three "fixability" states for a vuln:
 * complete fix, partial fix, or no fix.
 */
const PackageVersionVulnerabilityDrawerRemediationSuggestion: VoidFunctionComponent<Props> = ({
  type,
  version,
  upgradeDistance,
  useFixBadge, // Whether to show the fix badge.  Useful for up until partial fix is enabled
}) => {
  const heading = getHeading({ type, version });
  const hasFix = !['no-fix', 'unable-to-calculate'].includes(type);
  return (
    <div className={`${styles.remediationSuggestion} ${REMEDIATION_STYLE[type]}`}>
      <div className={styles.remediationSuggestionHeading}>
        <h5>{heading}</h5>
        {hasFix && version && useFixBadge && (
          <FixBadge version={version} distance={upgradeDistance} />
        )}
        {hasFix && <Badge color={REMEDIATION_BADGE_COLOR[type]}>{REMEDIATION_NAME[type]}</Badge>}
      </div>
      <p className={styles.remediationSuggestionParagraph}>{REMEDIATION_TEXT[type]}</p>
    </div>
  );
};

export default PackageVersionVulnerabilityDrawerRemediationSuggestion;
